import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Markdown } from "../components/Markdown";
import { Target, ArrowRight, BookOpen } from "lucide-react";

export default function PublicPlan() {
  const { slug } = useParams();
  const [plan, setPlan] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.get(`/public/plans/${slug}`).then((r) => setPlan(r.data)).catch(() => setMissing(true));
  }, [slug]);

  return (
    <div className="min-h-screen bg-background" data-testid="public-plan-page">
      <header className="border-b border-border">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="public-brand-link">
            <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-mono text-xs font-bold">MT</span>
            </div>
            <div className="text-sm font-semibold tracking-tight">Executives by Mahakali Tribunal</div>
          </Link>
          <Button asChild className="rounded-full" data-testid="public-plan-header-cta">
            <Link to="/auth?mode=signup">Build your own plan</Link>
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12 grid lg:grid-cols-3 gap-8">
        {missing ? (
          <Card className="p-10 lg:col-span-3 text-center" data-testid="public-plan-missing">This plan link is no longer available.</Card>
        ) : !plan ? (
          <Skeleton className="h-96 lg:col-span-3" />
        ) : (
          <>
            <section className="lg:col-span-2 space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
              <div>
                <div className="label-caps mb-2 flex items-center gap-1.5"><Target className="h-3 w-3 text-emerald-400" />30 · 60 · 90 day plan</div>
                <h1 className="text-4xl sm:text-5xl tracking-tight font-semibold" data-testid="public-plan-title">{plan.role_title}</h1>
                <p className="text-muted-foreground mt-3">
                  {[plan.stage, plan.industry].filter(Boolean).join(" · ")}{plan.author_name ? ` — shared by ${plan.author_name}` : ""}
                </p>
              </div>
              <Card className="p-7"><Markdown text={plan.content} testId="public-plan-content" /></Card>
              {plan.sources_used?.length > 0 && (
                <div className="text-xs text-muted-foreground space-y-1" data-testid="public-plan-sources">
                  <div className="label-caps mb-2 flex items-center gap-1.5"><BookOpen className="h-3 w-3" />Grounded in the Tribunal library</div>
                  {plan.sources_used.map((s, i) => <div key={i} className="font-mono">[{i + 1}] {s.title}</div>)}
                </div>
              )}
            </section>
            <aside className="lg:sticky lg:top-8 h-fit">
              <Card className="p-7 bg-primary/5 border-primary/30" data-testid="public-plan-cta-card">
                <div className="label-caps text-primary mb-3">Your turn</div>
                <h2 className="text-lg font-semibold tracking-tight">Walk into your next role with a plan like this.</h2>
                <p className="text-sm text-muted-foreground mt-2">
                  Generate a 30/60/90 plan for any executive seat, grounded in 271 volumes of leadership practice. Free to start.
                </p>
                <Button asChild className="rounded-full w-full mt-6 group" data-testid="public-plan-signup-btn">
                  <Link to="/auth?mode=signup">Build your own plan<ArrowRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-1" /></Link>
                </Button>
              </Card>
            </aside>
          </>
        )}
      </main>
    </div>
  );
}
